
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Consent management',
  layout: 'guide',
  description: 'Manage user consent and access authorization records',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Consent management' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "overview"
      }}>{`Overview`}</h2>
      <p>{`Plaid provides the ability to view authorization records and manage consent through the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management/api"
        }}>{`Consent API`}</a>{` and the Data Partner Dashboard. Using these tools, you can build a customer-facing consent management dashboard as well as audit and review consent records. `}</p>
      <h2 {...{
        "id": "authorization-records"
      }}>{`Authorization records`}</h2>
      <p>{`An authorization record captures the full context of a user's consent:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Application details:`}</strong>{` Which app has access (name, ID, logo)`}</li>
        <li parentName="ul"><strong parentName="li">{`Accounts shared:`}</strong>{` Specific accounts the user authorized`}</li>
        <li parentName="ul"><strong parentName="li">{`Data types:`}</strong>{` What data the app can access (transactions, balances, identity, etc.)`}</li>
        <li parentName="ul"><strong parentName="li">{`Consent timestamp:`}</strong>{` When the user granted authorization`}</li>
        <li parentName="ul"><strong parentName="li">{`Connection status:`}</strong>{` Active, revoked, or expired`}</li>
      </ul>
      <h3 {...{
        "id": "accessing-authorization-records"
      }}>{`Accessing authorization records`}</h3>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Method`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Best for`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Availability`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td"><a parentName="strong" {...{
                  "href": "/core-exchange/docs/consent-management/api"
                }}>{`Consent API`}</a></strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Automated systems, high volume`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`All integration models`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Data Partner Dashboard`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Manual review, low volume`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Single institution only`}</td>
          </tr>
        </tbody>
      </table>
      <Callout mdxType="Callout">
        <p>{`The no-code Dashboard interface is only available for single-institution accounts. Platform accounts must use the `}<a parentName="p" {...{
            "href": "/core-exchange/docs/consent-management/api"
          }}>{`Consent API`}</a>{`.`}</p>
      </Callout>
      <h3 {...{
        "id": "consent-api-beta"
      }}>{`Consent API (beta)`}</h3>
      <p>{`Build a consumer-facing consent portal, answer support requests about a customer's connections, and revoke access programmatically. Because it follows the FDX consent grant model, the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management/api"
        }}>{`Consent API`}</a>{` allows you to build an integration supporting multiple data access platforms, rather than locking you into a Plaid-specific schema.`}</p>
      <Callout mdxType="Callout">
        <p>{`The Consent API is currently in beta. To request access, contact Plaid solutions engineering. Note that the no-code Dashboard does not currently support the Consent API; if using both surfaces, you may see inconsistent data across the two.`}</p>
      </Callout>
      <p>{`See the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management/api"
        }}>{`Consent API reference`}</a>{` for endpoints, request and response fields, data clusters, webhooks, and Sandbox testing.`}</p>
      <p>{`The `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management/permissions-manager"
        }}>{`Permissions Manager API`}</a>{` is the legacy, Plaid-proprietary predecessor to the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management/api"
        }}>{`Consent API`}</a>{`. It remains supported for integrations already built on it, and its reference page covers the same guidance as this page in its own schemas.`}</p>
      <h3 {...{
        "id": "data-partner-dashboard-single-institution-only"
      }}>{`Data Partner Dashboard (single institution only)`}</h3>
      <p>{`For single-institution accounts, use the Permissions Manager tabs of Plaid's Data Partner Dashboard to search authorization records by user identifier. Browse connections, view details, and manage access via the web, no API integration needed.`}</p>
      <p><strong parentName="p">{`How to access:`}</strong>{` Reach out to your Plaid contact to enable dashboard access.`}</p>
      <Image src='/assets/img/core-exchange/permissions-manager/lookup.png' alt='Dashboard search interface for looking up customer connections' caption='Search by customer to view authorization details' expandable={true} mdxType="Image" />
      <p>{`If you build your own consumer-facing consent portal (rather than relying solely on the Dashboard), you're responsible for keeping Plaid and connected apps in sync when a user revokes access on your domain; see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management/api#revocation-and-ecosystem-sync"
        }}>{`Revocation and ecosystem sync`}</a>{` on the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management/api"
        }}>{`Consent API`}</a>{` reference.`}</p>
      <h2 {...{
        "id": "plaid-portal-myplaidcom"
      }}>{`Plaid Portal (my.plaid.com)`}</h2>
      <p>{`Users can view and manage all their Plaid connections at `}<a parentName="p" {...{
          "href": "https://my.plaid.com"
        }}>{`my.plaid.com`}</a>{`. This consumer-facing portal shows:`}</p>
      <ul>
        <li parentName="ul">{`Which apps have access to their data`}</li>
        <li parentName="ul">{`Which accounts are shared with each app`}</li>
        <li parentName="ul">{`When users created connections`}</li>
        <li parentName="ul">{`Options to revoke access`}</li>
      </ul>
      <h2 {...{
        "id": "best-practices"
      }}>{`Best practices`}</h2>
      <ul>
        <li parentName="ul">{`Integrate with the `}<a parentName="li" {...{
            "href": "/core-exchange/docs/consent-management/api"
          }}>{`Consent API`}</a>{` or the dashboard to access authorization records`}</li>
        <li parentName="ul">{`Set refresh token expiration to 13+ months (allows buffer for reauthorization)`}</li>
        <li parentName="ul">{`Direct users to `}<a parentName="li" {...{
            "href": "https://my.plaid.com"
          }}>{`my.plaid.com`}</a>{` for self-service connection management`}</li>
      </ul>
      <p>{`If you're building against the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management/api"
        }}>{`Consent API`}</a>{` directly, see its `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management/api#best-practices"
        }}>{`best practices`}</a>{` for revocation and webhook guidance.`}</p>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 2,
        title: "Overview",
      },
{
        id: "authorization-records",
        level: 2,
        title: "Authorization records",
      },
{
        id: "accessing-authorization-records",
        level: 3,
        title: "Accessing authorization records",
      },
{
        id: "consent-api-beta",
        level: 3,
        title: "Consent API (beta)",
      },
{
        id: "data-partner-dashboard-single-institution-only",
        level: 3,
        title: "Data Partner Dashboard (single institution only)",
      },
{
        id: "plaid-portal-myplaidcom",
        level: 2,
        title: "Plaid Portal (my.plaid.com)",
      },
{
        id: "best-practices",
        level: 2,
        title: "Best practices",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
